/* eslint-disable react-hooks/exhaustive-deps */
import { useGetProfile } from "../../../hooks/useGetProfile"
import Placeholder from '../../../assets/images/placeholder.png'
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay } from 'swiper/modules'
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Project } from "../../../types/project.type"
import { resolveOptionalColor, resolveThemeColor } from "../../../utils/buttonColor"

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/autoplay'

function ProfileSection({ project }: { project: Project }) {
  const { getProfile, profiles } = useGetProfile()
  const { t } = useTranslation('home')

  const themeColor = resolveThemeColor(project.themeColor)
  const buttonColor = resolveThemeColor(project.buttonColor)
  const serviceColor = resolveOptionalColor(project.serviceColor)

  useEffect(() => {
    getProfile()
  }, [])

  if (!profiles?.length) return null

  return (
    <section
      className="mx-5 overflow-hidden rounded-2xl border border-gray-200/90 bg-white text-gray-900 shadow-xl shadow-gray-900/5"
      id="profile"
    >
      <header
        className="border-b border-gray-100 px-5 py-6 sm:px-8 sm:py-8"
        style={{
          background: `linear-gradient(135deg, ${themeColor}0d 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
        }}
      >
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900">{t('ourTeam')}</h2>
        <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-gray-600">
          {t('ourTeamSubtitle')}
        </p>
      </header>

      <div className="px-3 pb-8 pt-2 sm:px-6 sm:pb-10">
        <Swiper
          slidesPerView={1}
          spaceBetween={16}
          autoplay={{
            delay: 3200,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          modules={[Autoplay]}
          loop={profiles.length > 1}
          speed={700}
          breakpoints={{
            640: {
              slidesPerView: 1,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 2,
              spaceBetween: 24,
            },
            1280: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
          }}
          className="!pb-2"
        >
          {profiles.map((profile, index) => (
            <SwiperSlide key={profile.userId ?? index} className="!h-auto py-4 sm:py-5">
              <article
                className="flex h-full flex-col rounded-xl border border-gray-200/90 p-5 shadow-sm transition-shadow hover:shadow-md"
                style={{
                  borderLeftWidth: 4,
                  borderLeftColor: themeColor,
                  ...(serviceColor
                    ? {
                        background: `linear-gradient(145deg, ${serviceColor}10 0%, #ffffff 38%)`,
                      }
                    : { backgroundColor: '#ffffff' }),
                  borderTopColor: '#e5e7eb',
                  borderRightColor: '#e5e7eb',
                  borderBottomColor: '#e5e7eb',
                }}
              >
                <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
                  <div
                    className="shrink-0 rounded-full p-0.5 shadow-sm ring-2 ring-white"
                    style={{ background: `linear-gradient(135deg, ${themeColor}, ${buttonColor})` }}
                  >
                    <img
                      src={profile.profilePictureFile?.s3Url || Placeholder}
                      alt={profile.name}
                      className="h-24 w-24 rounded-full object-cover sm:h-28 sm:w-28"
                    />
                  </div>

                  <h3 className="mt-4 text-lg font-semibold leading-snug" style={{ color: themeColor }}>
                    {profile.name}
                    {profile.title ? (
                      <span className="font-medium text-gray-600">, {profile.title}</span>
                    ) : null}
                  </h3>

                  {profile.occupation ? (
                    <p className="mt-1 text-sm font-medium text-gray-600">{profile.occupation}</p>
                  ) : null}

                  <div
                    className="mx-auto mt-3 h-px w-12 sm:mx-0"
                    style={{ backgroundColor: themeColor, opacity: 0.45 }}
                    aria-hidden
                  />

                  {profile.description ? (
                    <p className="mt-4 text-sm leading-relaxed text-gray-600">{profile.description}</p>
                  ) : null}

                  {profile.service ? (
                    <div className="mt-5 w-full rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-left">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Layanan konsultasi
                      </p>
                      <p className="mt-1 text-sm text-gray-700">{profile.service}</p>
                    </div>
                  ) : null}
                </div>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
}

export default ProfileSection
